'use client'

import Image from 'next/image'
import {FC} from 'react'

import {BrandProtectionOverview} from '@/components/brand-monitoring/overview/BrandProtectionOverview'
import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {Section} from '@/components/common/content/Section'
import {Text, TextVariant} from '@/components/common/content/Text'
import {Hero} from '@/components/common/hero/Hero'
import {HeroBenefitList} from '@/components/common/hero/HeroBenefitList'
import {HowItWorks} from '@/components/common/how-it-works/v2/HowItWorks'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {LicensedAttorneys} from '@/components/common/team/LicensedAttorneys'
import {CountriesOffer} from '@/components/homepage/countries/CountriesOffer'
import {OurClientsAndStatistics} from '@/components/homepage/OurClientsAndStatistics'
import {Page} from '@/components/Page'
import {TmRenewalE2eProtectionCards} from '@/components/trademark-renewal/TmRenewalE2eProtectionCards'
import {TmRenewalPortalOverview} from '@/components/trademark-renewal/TmRenewalPortalOverview'
import {getContactContent} from '@/content/contact'
import {useHomepageContent} from '@/content/homepage'
import {
  getTrademarkRenewalContent,
  getTrademarkRenewalJurisdictionContent,
} from '@/content/trademark-renewal/trademarkRenewal'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {Jurisdiction} from '@/utils/countries/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type TrademarkRenewalJurisdictionProps = {
  numOfRegisteredTrademarks: number
  jurisdiction: Jurisdiction
  mostRecentArticles: RecentArticleCard[]
}

const TrademarkRenewalJurisdiction: FC<TrademarkRenewalJurisdictionProps> = ({
  numOfRegisteredTrademarks,
  jurisdiction,
  mostRecentArticles,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {hero, howItWorks, getStartedBanner} = getTrademarkRenewalContent(language)
  const {hero: heroJurisdiction} = getTrademarkRenewalJurisdictionContent(jurisdiction, language)
  const {countriesOffer} = useHomepageContent()
  const {consultationLinkHref, consultationLinkText} = getContactContent()
  const isEnLanguage = language === Locale.en

  return (
    <>
      <Page>
        <Hero
          title={
            <Trans
              i18nKey={heroJurisdiction.title}
              values={{jurisdiction: alpha2ToName(jurisdiction, language)}}
              components={{
                1: (
                  <Text variant={TextVariant.h1} color={theme.palette.primary.main} element="span">
                    {alpha2ToName(jurisdiction, language)}
                  </Text>
                ),
              }}
            />
          }
          subtitle={t(hero.subtitle)}
          primaryButtonText={t(hero.primaryButtonText)}
          primaryButtonLink={heroJurisdiction.primaryButtonLink}
          primaryButtonTrackedEvent={`Click on Start with trademark renewal (Trademark renewal ${jurisdiction} - Hero)`}
          secondaryButtonText={t(consultationLinkText)}
          secondaryButtonLink={consultationLinkHref}
          secondaryButtonTrackedEvent={`Click on Show price list (Trademark renewal ${jurisdiction} - Hero)`}
          benefits={<HeroBenefitList list={hero.list} />}
          alignItems="center"
        >
          <div className="image">
            <Image
              src={heroJurisdiction.imageSrc}
              alt={heroJurisdiction.imageAlt}
              sizes={`${MOBILE_SCREENSIZE} 270px, ${TABLET_SCREENSIZE} 280px, 360px`}
              fill
              priority
              fetchPriority="high"
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <OurClientsAndStatistics
          numOfRegisteredTrademarks={numOfRegisteredTrademarks}
          backgroundColor={theme.palette.background.default}
          backgroundOverlay={{color: theme.palette.background.butter, heightRatio: 0.5}}
          hasBoxShadow
        />
        <Section hasPaddingTop>
          <TmRenewalE2eProtectionCards jurisdiction={jurisdiction} />
        </Section>
        <CountriesOffer
          description={countriesOffer.description}
          trackedEventParameters={{position: 'Countries offer - Trademark renewal', jurisdiction}}
        />
        <Section hasPaddingTop>
          <LicensedAttorneys isReversed />
        </Section>
        <Section hasPaddingTop hasPaddingBottom>
          <HowItWorks title={t(howItWorks.title)} steps={howItWorks.steps} isCentered />
        </Section>
        <TmRenewalPortalOverview />
        {isEnLanguage && (
          <BrandProtectionOverview
            videoTrackedEventParameters={{
              section: 'Monitoring platforms',
              page: 'Trademark renewal',
            }}
            backgroundColor={theme.palette.background.default}
          />
        )}
        {mostRecentArticles.length > 0 && (
          <Section hasPaddingTop={!isEnLanguage} hasPaddingBottom>
            <MostRecentArticles
              articles={mostRecentArticles}
              trackedEvent={{name: 'RecentBlogs-TrademarkRenewal', parameters: {jurisdiction}}}
            />
          </Section>
        )}
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={heroJurisdiction.primaryButtonLink}
          primaryButtonText={t(getStartedBanner.primaryButtonText)}
          primaryButtonTrackedEvent={`GetStartedBanner-TrademarkRenewal${jurisdiction}`}
        />
      </Page>

      <style jsx>{`
        .image {
          position: relative;
          width: 360px;
          height: 450px;
          margin-left: ${theme.spacing[8]};
          margin-right: ${theme.spacing[4]};
        }
        @media ${TABLET_SCREENSIZE} {
          .image {
            width: 280px;
            height: 350px;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 270px;
            height: 250px;
          }
        }
      `}</style>
    </>
  )
}

export default TrademarkRenewalJurisdiction
